import React, { useState, useEffect } from 'react';
import {
    Form,
    Modal,
} from 'react-bootstrap';
import { DataTable, Input, PageWrapper } from '../../components';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createRole, getAdminPortalRoleListWithParams, getPermissionList, updateRole } from '../../api/role';
import { useForm, FormProvider } from "react-hook-form";
import CheckboxTree from 'react-checkbox-tree';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { FaRegSquare, FaChevronDown, FaSquareCheck, FaSquareMinus, FaChevronUp } from "react-icons/fa6";
import { RiAddLargeLine } from 'react-icons/ri';
import Button from '../../components/common/Button';

const RolePermission = () => {
    const { t } = useTranslation()
    const navigate = useNavigate();
    const form = useForm()
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25 });
    const [totalPages, setTotalPage] = useState(0);
    const [data, setData] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [selectedRole, setSelectedRole] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [checked, setChecked] = useState([]);
    const [expanded, setExpanded] = useState([]);

    const columns = [
        { key: "name", label: t("NAME"), },
        {
            key: "action", label: t("ACTION"), render: (v, data) => <Dropdown>
                <Dropdown.Toggle variant="primary" size="sm" className='py-0'></Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => onEditClick(data)}>{t("EDIT")}</Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        }
    ]

    useEffect(() => {
        getData(pagination)
        getPermissions()
    }, [pagination])

    const getData = async (pagination) => {
        const response = await getAdminPortalRoleListWithParams({ skip: (pagination?.page - 1) * pagination?.pageSize, limit: pagination?.pageSize })
        setData(response.items)
        setTotalPage(Math.ceil(response?.pagination?.total / pagination?.pageSize))
    }

    const getPermissions = async () => {
        const response = await getPermissionList()
        delete response.success
        setPermissions(response)
    }

    const onEditClick = (selected) => {
        setIsEdit(true);
        setChecked(convertPermissionsToChecked(selected.permission))
        form.reset(selected)
        setExpanded([])
        setSelectedRole(selected)
    }

    const onAddClick = () => {
        setIsEdit(false);
        form.reset({ name: "" })
        setChecked([])
        setExpanded([])
        setSelectedRole({ name: "" })
    }

    const create = async (data) => {
        const response = await createRole({ name: data.name, permission: convertCheckedToPermissions(checked) })
        if (response.success) {
            setSelectedRole(null)
            getData(pagination)
        }
    }

    const update = async (data) => {
        const response = await updateRole(data.id, { name: data.name, permission: convertCheckedToPermissions(checked) })
        if (response.success) {
            setSelectedRole(null)
            getData(pagination)
        }
    }

    function convertPermissionsToTreeData(permissions) {
        return Object.entries(permissions).map(([key, actions]) => ({
            value: key,
            label: key,
            children: actions.map((action) => ({
                value: `${key}-${action}`,
                label: action
            }))
        }));
    }

    function convertPermissionsToChecked(permissions) {
        return Object.entries(permissions).flatMap(([key, actions]) =>
            actions.map((action) => `${key}-${action}`)
        );
    }

    function convertCheckedToPermissions(checked) {
        return checked.reduce((permissions, value) => {
            const firstHyphenIndex = value.indexOf('-');
            const key = value.slice(0, firstHyphenIndex);
            const action = value.slice(firstHyphenIndex + 1);
            if (!permissions[key]) {
                permissions[key] = [];
            }
            permissions[key].push(action);
            return permissions;
        }, {});
    }

    return (
        <PageWrapper
            headerProps={{
                title: "ROLE_PERMISSION",
                rightElement: <Button icon={<RiAddLargeLine />} onClick={onAddClick}>{t("ADD")}</Button>
            }}
        >
            <DataTable columns={columns} data={data} pagination={{ ...pagination, totalPages, onChange: setPagination }} />

            <Modal show={selectedRole} onHide={() => setSelectedRole(null)}>
                <FormProvider {...form}>
                    <Form onSubmit={form.handleSubmit(isEdit ? update : create)}>
                        <Modal.Header closeButton>
                            <Modal.Title>{t(isEdit ? "EDIT_ROLE" : "ADD_ROLE")}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ height: "76vh", overflow: "auto" }}>
                            <Input label={t("NAME")} name={"name"} rules={{ required: t("NAME_IS_REQUIRED") }} />

                            <CheckboxTree
                                nodes={convertPermissionsToTreeData(permissions)}
                                checked={checked}
                                expanded={expanded}
                                onCheck={(checked) => setChecked(checked)}
                                onExpand={(expanded) => setExpanded(expanded)}
                                icons={{
                                    check: <FaSquareCheck color='blue' />,
                                    uncheck: <FaRegSquare />,
                                    halfCheck: <FaSquareMinus color='blue' />,
                                    expandClose: <FaChevronDown />,
                                    expandOpen: <FaChevronUp />,
                                    expandAll: <FaChevronDown />,
                                    collapseAll: <FaChevronUp />,
                                    parentClose: null,
                                    parentOpen: null,
                                    leaf: null
                                }}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setSelectedRole(null)}>
                                {t("CLOSE")}
                            </Button>
                            <Button variant="primary" type='submit'>
                                {t("SAVE")}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </FormProvider>
            </Modal>
        </PageWrapper>
    );
};

export default RolePermission;
